import React from 'react';
import PropTypes from 'prop-types';
import { ApolloProvider } from 'react-apollo';
import { Query } from 'react-apollo';
import { Provider } from 'react-redux';
import gql from 'graphql-tag';
import ApolloClient from 'apollo-client';
import { createHttpLink } from 'apollo-link-http';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { createStore } from 'redux';

import ProgressIndicator from './ProgressIndicator';
import ContactInformationStep from './ContactInformationStep';
import ContactInformationCreateUserStep from './ContactInformationCreateUserStep';
import MfaEnrollmentStep from './MfaEnrollmentStep';
import MotivationStep from './MotivationStep';
import ProductionInformationStep from './ProductionInformationStep';
import PracticeInformationStep from './PracticeInformationStep';
import PandCLinesOfBusinessStep from './PandCLinesOfBusinessStep';
import HeadlineAndAboutMeStep from './HeadlineAndAboutMeStep';
// OnboardingProvider removed - OnboardingWizard manages its own state via Redux
// Step components fall back to props when context is not available
import SwitchOnboardingLanguage from './SwitchOnboardingLanguage';
import OnboardingThemeProvider from './OnboardingThemeProvider';
import { ActivationCheckEmailPage } from '../../containers/ActivationCheckEmailContainer';

// Header component for consistent styling across steps
const OnboardingHeader = ({ currentLanguage, onLanguageChange, companySettings }) => {
  const headerText = currentLanguage === 'fr' ? 
    "Complétez votre profil" : 
    "Complete Your Profile";
  
  const companyName = companySettings?.branded_name || 'FindBob';
  
  const subtitleText = currentLanguage === 'fr' ? 
    `Configurons votre compte pour que vous puissiez tirer le meilleur parti de ${companyName}.` : 
    `Let's get your account set up so you can get the most out of ${companyName}.`;
  
  return (
    <div className="relative">
      <div className="absolute top-0 right-0">
        <SwitchOnboardingLanguage 
          currentLanguage={currentLanguage} 
          onLanguageChange={onLanguageChange}
          supportedLocales={['en', 'fr']}
        />
      </div>
      <div className="text-center mb-10 border-b pb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-3 tracking-tight">{headerText}</h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">{subtitleText}</p>
      </div>
    </div>
  );
};

// Define onboarding steps with component and path information
const STEPS = [
  {
    name: 'Contact Information',
    fr_name: 'Coordonnées',
    component: ContactInformationStep,
    path: 'contact_information'
  },
  {
    // Conditional — only visible when MFA enrollment is required for this user
    // (Identity provider + require_mfa_for_identity + not yet enrolled). Gated
    // in getVisibleSteps via mfaRequired so it's invisible for every other
    // tenant/user. The server-side backstop enforces it regardless of the
    // client-side step list.
    name: 'Security',
    fr_name: 'Sécurité',
    component: MfaEnrollmentStep,
    path: 'mfa_enrollment'
  },
  {
    name: 'Motivation',
    fr_name: 'Motivation',
    component: MotivationStep,
    path: 'motivation'
  },
  { 
    name: 'Production Information', 
    fr_name: 'Informations de production',
    component: ProductionInformationStep,
    path: 'production_information'
  },
  { 
    name: 'Practice Information', 
    fr_name: 'Informations de pratique',
    component: PracticeInformationStep,
    path: 'practice_information'
  },
  { 
    name: 'P&C Lines of Business', 
    fr_name: 'Secteurs d\'activité P&C',
    component: PandCLinesOfBusinessStep,
    path: 'pnc_lines_of_business'
  },
  { 
    name: 'Headline', 
    fr_name: 'Titre',
    component: HeadlineAndAboutMeStep,
    path: 'headline_and_about_me'
  }
];

function normalizeFeatureList(features) {
  if (Array.isArray(features)) return features.map(String);
  if (features && typeof features === 'object') {
    // Sometimes we store features as a hash of { featureName: true/false }
    return Object.keys(features).filter((k) => features[k]).map(String);
  }
  return [];
}

function isModernMotivationModalEnabledFromFeatures(features) {
  const list = normalizeFeatureList(features);
  return list.includes('modernized_onboarding_home_page') && list.includes('modernized_motivation_onboarding');
}

function isModernMotivationModalEnabledFromCompanySettings(companySettings) {
  if (!companySettings) return false;
  
  const result = (() => {
    if (companySettings.feature_types) {
      return companySettings.feature_types.includes('modernized_onboarding_home_page') &&
             companySettings.feature_types.includes('modernized_motivation_onboarding');
    }
    return isModernMotivationModalEnabledFromFeatures(companySettings.features);
  })();

  return result;
}

function companyRequiresMfa(companySettings) {
  return normalizeFeatureList(
    (companySettings && companySettings.feature_types) || (companySettings && companySettings.features)
  ).includes('require_mfa_for_identity');
}

function getVisibleSteps({ businessType = 'other', skipMotivation = false, mfaRequired } = {}) {
  // mfaRequired is server-authoritative (PublicUserType#mfa_enrollment_required,
  // backed by MfaGate). Callers that don't thread it through fall back to two
  // signals so every existing call site picks up the right answer without a
  // signature change:
  //   1. the value stashed on the wizard-data global (set from GraphQL / on
  //      leaving step 1), and
  //   2. the URL itself — the server-side backstop only ever redirects an
  //      un-enrolled Identity user to /onboarding/mfa_enrollment, so being on
  //      that path is sufficient proof the step belongs in the list even before
  //      the async user query resolves.
  const onMfaUrl = typeof window !== 'undefined' &&
    /\/onboarding\/mfa_enrollment/.test(window.location.pathname);
  const mfaOn = (mfaRequired !== undefined)
    ? !!mfaRequired
    : (onMfaUrl || !!(typeof window !== 'undefined' && window.__WIZARD_DATA__ && window.__WIZARD_DATA__.mfaRequired));
  return STEPS.filter((step) => {
    if (step.path === 'mfa_enrollment') return mfaOn;
    if (step.path === 'pnc_lines_of_business') return businessType === 'p_and_c';
    if (skipMotivation && step.path === 'motivation') return false;
    return true;
  });
}

function mapLegacyStepToVisibleStep({ legacyStep, businessType = 'other', skipMotivation = false }) {
  const oldSteps = getVisibleSteps({ businessType, skipMotivation: false });
  const newSteps = getVisibleSteps({ businessType, skipMotivation });
  const idx = (parseInt(legacyStep, 10) || 1) - 1;
  const legacyPath = oldSteps[idx] && oldSteps[idx].path;
  if (!legacyPath) return 1;
  const newIndex = newSteps.findIndex((s) => s.path === legacyPath);
  if (newIndex >= 0) return newIndex + 1;
  // If the legacy step is the removed motivation step, advance to the next valid step (production)
  if (skipMotivation && legacyPath === 'motivation') {
    const productionIndex = newSteps.findIndex((s) => s.path === 'production_information');
    return productionIndex >= 0 ? productionIndex + 1 : 1;
  }
  return 1;
}

// Helper function to find step by path (against a visible step list)
function findStepByPath(path, visibleSteps = []) {
  if (!path) return 1;
  const cleanPath = path.replace(/^\/+|\/+$/g, ''); // Remove leading/trailing slashes
  const stepIndex = visibleSteps.findIndex((step) =>
    step.path === cleanPath || `onboarding/${step.path}` === cleanPath
  );
  return stepIndex >= 0 ? stepIndex + 1 : 1;
}

// Event listener for popstate (browser back/forward navigation)
window.addEventListener('popstate', function(event) {
  const pathMatch = window.location.pathname.match(/\/onboarding\/([^\/]+)/);
  const path = pathMatch ? pathMatch[1] : null;
  
  if (path && window.FindBobOnboarding) {
    // Get the business type from global wizard data if available
    const businessType = window.__WIZARD_DATA__?.companySettings?.business_type || 'other';
    const skipMotivation = isModernMotivationModalEnabledFromCompanySettings(window.__WIZARD_DATA__?.companySettings);
    
    const filteredSteps = getVisibleSteps({ businessType, skipMotivation });
    
    // Find step in filtered steps
    const stepIndex = filteredSteps.findIndex(step => 
      step.path === path || 
      `onboarding/${step.path}` === path
    );
    
    if (stepIndex >= 0) {
      const stepNumber = stepIndex + 1;
      // Get company ID and save step
      const companyId = window.__WIZARD_DATA__?.companySettings?.id || 
                       window.__WIZARD_DATA__?.companySettings?.name ||
                       'default';
      saveOnboardingProgress(stepNumber, companyId);
      
      // Reload the page to ensure the component updates
      // This is needed because the Redux store might be in a different state
      window.location.reload();
    }
  }
});

// Query for user data that exists in the schema
const USER_QUERY = gql`
  query getCurrentUser {
    currentUserInfoAuth
    currentUserASubscriber
    company {
      is_managed
      explicit_opt_in_tos_and_pp
      country
      supported_locales
      agent_word
      has_billing
      business_type
      has_agency_name
      has_county
      ask_transition_readiness_on_signup
      skip_product_mix_on_signup
      hide_product_mix_on_profile
      ask_me_about_mandatory
      stylesheet_name
      show_planning_preference_question
      branded_name
      logo
      ask_me_about_question
      imported_production_data_read_only
      use_birth_year_instead_of_dob
      iiroc_licensing_options
      use_canadian_retail_wealth_product_mix
      use_p_and_c_product_mix
      planning_preference_already_exists_question
      catastrophic_planning_word
      request_aum_on_sutd
      allow_user_affiliations
      imported_production_data_read_only
      enabled_student_profiles
      student_word
      student_program_title
      available_primary_markets
      max_revenue_input
      revenue_line_terminology
      features
      feature_types
      business_continuity_states {
        id
        name
      }
      succession_planning_states {
        id
        name
      }
      identity_authentication_enabled
      opt_in_scroll_accept_tos_privacy_policy
    }
    getCurrentUser {
      id
      email
      name
      phone
      postal_code
      user_name
      agency_name
      annual_revenue
      assets_under_management
      bio
      birth_date
      city
      county
      number_of_clients
      profile_picture
      province
      recurring_revenue
      business_continuity_state_id
      succession_planning_state_id
      created_at
      fee_based_revenue
      trailer_revenue
      direct_revenue
      transactional_revenue
      planning_preference
      completed_step_1
      mfa_enrollment_required
      is_branch_exception
      talent_acquisition_user
      affiliations { 
        id
        name
      }
      company {
        is_managed
        explicit_opt_in_tos_and_pp
        skip_product_mix_on_signup
        hide_product_mix_on_profile
        branded_name
        identity_authentication_enabled
        business_type
        use_canadian_retail_wealth_product_mix
        use_p_and_c_product_mix
        planning_preference_already_exists_question
        enabled_student_profiles
        student_word
        student_program_title
        catastrophic_planning_word
        imported_production_data_read_only
      }
      headline
      avg_client_age
      nick_name
      is_student
      student_url_video
      student_url_resume
      student_url_linkedin
      student_program_year
      primary_markets
      roles
      designations
      tag_list
      transition_goals
      advisor_licenses
      in_auction
      years_practicing
      number_of_employees
      percent_revenue_recurring
      percent_non_recurring_revenue
      percent_fee_based
      percent_commission
      gross_production
      mix_insurance
      mix_mutual_funds
      mix_annuities
      mix_other
      postal_code
      email
      locale
      motivation
      recognition_level
      product_mix {
        life_ifp
        home_ifp
        auto_ifp
        commercial_ifp
        health_ifp
        group_ifp
        mutual_funds_aum
        securities_aum
        annuities_aua
        segregated_funds_aua
        total_assets
        mcpi_ifp
        private_products_ifp
        crop_hail_ifp
        crop_hail_with_wind_ifp
        farm_insurance_ifp
        price_products_ifp
        replant_supplement_ifp
        gic_fixed_income
        insurance
        equity
      }
      headline_completed
      personal_attributes_complete
      practice_complete
      compensation_complete
      product_mix_complete
      transition_goals_complete
      revenue_complete
      planning_info_complete
      transition_period_months
      target_timeframe
      workweek_trajectory_now
      workweek_trajectory_year_3
      workweek_trajectory_year_5
      workweek_trajectory_year_10
      workweek_trajectory_year_15
      workweek_trajectory_year_20
      workweek_trajectory_year_25
      inventory_user {
        id
        recognition_level
        is_invisible
        years_with_company
      }
    }
    availableAvatars
    availableDesignations {
      id
      name
      fr_name
    }
    availableProducts
    availableStrengths
    availableAdvisorLicenses
    getPrivacyPolicy
    getTermsOfService
    availableAffiliations {
      id
      name
    }
  }
`;

// Create Redux reducer
function wizardReducer(state = {
  currentStep: 1,
  userData: {},
  companySettings: {}
}, action) {
  switch (action.type) {
    case 'SET_CURRENT_STEP':
      // Validate step is within bounds
      const stepNumber = parseInt(action.payload) || 1;
      const validStep = Math.min(Math.max(1, stepNumber), STEPS.length);
      
      // Save to localStorage whenever step changes with company-specific key
      const companyId = state.companySettings?.id || 
                       state.companySettings?.name ||
                       window.__WIZARD_DATA__?.companySettings?.id || 
                       window.__WIZARD_DATA__?.companySettings?.name ||
                       'default';
      saveOnboardingProgress(validStep, companyId);
      return { ...state, currentStep: validStep };
    case 'UPDATE_USER_DATA':
      return { ...state, userData: { ...state.userData, ...action.payload } };
    case 'UPDATE_COMPANY_SETTINGS':
      return { ...state, companySettings: { ...state.companySettings, ...action.payload } };
    default:
      return state;
  }
}

// Helper functions for company-specific onboarding progress with timeout
function getOnboardingStorageKey(companyId) {
  return `findbob_onboarding_step_${companyId || 'default'}`;
}

function saveOnboardingProgress(step, companyId) {
  const validStep = parseInt(step) || 1;
  if (validStep >= 1 && validStep <= STEPS.length) {
    const skipMotivation = isModernMotivationModalEnabledFromCompanySettings(window.__WIZARD_DATA__?.companySettings);
    const progressData = {
      step: validStep,
      timestamp: Date.now(),
      companyId: companyId || 'default',
      skipMotivation: !!skipMotivation,
      schemaVersion: 2,
    };
    localStorage.setItem(getOnboardingStorageKey(companyId), JSON.stringify(progressData));
    
    // Also save to server for persistence across sessions
    saveOnboardingProgressToServer(validStep);
  }
}

// Save progress to server (fire-and-forget, don't block UI)
function saveOnboardingProgressToServer(step) {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
  
  fetch('/onboarding/save_progress', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken || '',
    },
    body: JSON.stringify({ step }),
    credentials: 'same-origin',
  }).catch(error => {
    // Silently fail - localStorage will still work for short-term persistence
    console.warn('Failed to save onboarding progress to server:', error);
  });
}

function getOnboardingProgressData(companyId) {
  const storageKey = getOnboardingStorageKey(companyId);
  const progressData = localStorage.getItem(storageKey);
  
  if (!progressData) {
    return null;
  }
  
  try {
    const parsed = JSON.parse(progressData);
    const now = Date.now();
    const thirtyMinutesAgo = now - (30 * 60 * 1000); // 30 minutes in milliseconds
    
    // Check if progress is older than 30 minutes
    if (parsed.timestamp < thirtyMinutesAgo) {
      // Clear expired progress
      localStorage.removeItem(storageKey);
      return null;
    }
    
    // Check if company matches (additional safety check)
    if (parsed.companyId !== (companyId || 'default')) {
      return null;
    }
    
    return parsed;
  } catch (error) {
    // Invalid data, clear it
    localStorage.removeItem(storageKey);
    return null;
  }
}

function getOnboardingProgress(companyId) {
  const data = getOnboardingProgressData(companyId);
  return data ? data.step : null;
}

function clearOnboardingProgress(companyId) {
  const storageKey = getOnboardingStorageKey(companyId);
  localStorage.removeItem(storageKey);
}

function cleanupExpiredOnboardingProgress() {
  // Clean up any expired onboarding progress from localStorage
  const allKeys = Object.keys(localStorage);
  const onboardingKeys = allKeys.filter(key => key.startsWith('findbob_onboarding_step_'));
  
  onboardingKeys.forEach(key => {
    try {
      const progressData = localStorage.getItem(key);
      if (progressData) {
        const parsed = JSON.parse(progressData);
        const now = Date.now();
        const thirtyMinutesAgo = now - (30 * 60 * 1000);
        
        // Remove if older than 30 minutes
        if (parsed.timestamp < thirtyMinutesAgo) {
          localStorage.removeItem(key);
        }
      }
    } catch (error) {
      // Invalid data, remove it
      localStorage.removeItem(key);
    }
  });
}

// Helper function to check if user data is valid and complete
function isUserDataValid(userData) {
  if (!userData) return false;
  
  // Check for essential user identifiers
  const hasIdentity = userData.id || userData.email;
  
  // Check if user data seems corrupted or incomplete
  if (!hasIdentity) return false;
  
  return true;
}

// Helper function to reset onboarding when user data is invalid
function resetOnboardingForInvalidUser(store, companyId) {
  // Clear saved progress
  clearOnboardingProgress(companyId);
  
  // Reset to step 1
  store.dispatch({ type: 'SET_CURRENT_STEP', payload: 1 });
  
  // Clear user data from store
  store.dispatch({ type: 'UPDATE_USER_DATA', payload: {} });
  
  // Update URL to reflect reset
  window.history.replaceState(
    { step: 1 }, 
    'Onboarding - Contact Information', 
    '/onboarding/contact_information'
  );
}

// Function to save current step to localStorage directly (for use outside of Redux)
function saveCurrentStep(step) {
  const validStep = parseInt(step) || 1;
  if (validStep >= 1 && validStep <= STEPS.length) {
    // Try to get company ID from window data
    const companyId = window.__WIZARD_DATA__?.companySettings?.id || 
                     window.__WIZARD_DATA__?.companySettings?.name ||
                     'default';
    saveOnboardingProgress(validStep, companyId);
  }
}

// Function to clear onboarding progress
function resetOnboardingProgress() {
  // Try to get company ID from window data
  const companyId = window.__WIZARD_DATA__?.companySettings?.id || 
                   window.__WIZARD_DATA__?.companySettings?.name ||
                   'default';
  clearOnboardingProgress(companyId);
}

// Helper to robustly get company settings merging store and window data
function getEffectiveCompanySettings(storeSettings) {
  const windowSettings = window.__INITIAL_WIZARD_DATA__?.companySettings || window.__WIZARD_DATA__?.companySettings || {};
  const settings = storeSettings || {};
  
  // Merge strategies: prefer store, but fallback to window for arrays/objects if missing in store or empty
  // This protects against GraphQL updates potentially missing feature flags
  return {
    ...windowSettings,
    ...settings,
    feature_types: (settings.feature_types && settings.feature_types.length > 0) 
      ? settings.feature_types 
      : (windowSettings.feature_types || []),
    features: (settings.features && (Array.isArray(settings.features) ? settings.features.length > 0 : Object.keys(settings.features).length > 0))
      ? settings.features
      : (windowSettings.features || {})
  };
}

// Inner component that handles the GraphQL query - using class component
class OnboardingWizardInner extends React.Component {
  constructor(props) {
    super(props);
    
    // Language resolution order:
    //   1. localStorage (explicit override by SwitchOnboardingLanguage — only stored
    //      when the user picks something DIFFERENT from the tenant default; see
    //      handleLanguageChange which removes the entry when toggling back)
    //   2. companySettings.default_locale (tenant-authoritative — users being
    //      onboarded to a UK tenant see UK copy regardless of personal user.locale,
    //      which may carry the 'en' default from a cross-tenant account or a
    //      migration before set_default_locale_from_company was in place)
    //   3. window.locale (railsContext.i18nLocale — reflects ApplicationController#set_locale)
    //   4. current user's locale (final fallback)
    //   5. default 'en'
    // companySettings arrives via wizardData prop (from the outer OnboardingWizard
    // which reads window.__INITIAL_WIZARD_DATA__ at boot), with window globals
    // as secondary sources for code paths where props haven't propagated yet.
    const companyDefaultLocale =
      props.wizardData?.companySettings?.default_locale ||
      props.companySettings?.default_locale ||
      (typeof window !== 'undefined' &&
        (window.__INITIAL_WIZARD_DATA__?.companySettings?.default_locale ||
          window.__WIZARD_DATA__?.companySettings?.default_locale)) ||
      null;
    const userLocale = props.user?.locale;
    const serverLocale = (typeof window !== 'undefined' && window.locale) || null;
    const storedLanguage = localStorage.getItem('findbob_language');
    const savedLanguage =
      storedLanguage ||
      companyDefaultLocale ||
      serverLocale ||
      userLocale ||
      'en';
    
    this.state = {
      storeState: props.store.getState(),
      currentLanguage: savedLanguage,
      hideHeader: false
    };
    
    // Bind methods
    this.goToNextStep = this.goToNextStep.bind(this);
    this.goToPreviousStep = this.goToPreviousStep.bind(this);
    this.handleUserUpdate = this.handleUserUpdate.bind(this);
    this.handleLanguageChange = this.handleLanguageChange.bind(this);
    this.handleHideHeader = this.handleHideHeader.bind(this);
  }
  
  componentDidMount() {
    // Disable browser scroll restoration to prevent scroll position preservation on navigation
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // Scroll to top on mount
    window.scrollTo(0, 0);
    
    // Subscribe to Redux store updates
    this.unsubscribe = this.props.store.subscribe(() => {
      this.setState({ storeState: this.props.store.getState() });
    });
    
    // Process GraphQL data if already available
    this.processGraphQLData();
  }
  
  componentDidUpdate(prevProps) {
    // Check if GraphQL data changed
    if (this.props.graphqlData !== prevProps.graphqlData) {
      this.processGraphQLData();
    }

    // If the user's locale arrived via GraphQL (later than constructor) and
    // the user hasn't manually overridden via localStorage, consider adopting
    // it — but only when it differs from the tenant's default_locale. A user
    // with a stale user.locale='en' on a UK tenant (default_locale='en-GB')
    // shouldn't have GraphQL flip the wizard back from en-GB to en; the
    // onboarding experience is tenant-scoped.
    const prevLocale = prevProps.graphqlData?.getCurrentUser?.locale;
    const nextLocale = this.props.graphqlData?.getCurrentUser?.locale;
    const companyDefaultLocale =
      this.props.wizardData?.companySettings?.default_locale ||
      this.props.companySettings?.default_locale ||
      (typeof window !== 'undefined' &&
        (window.__INITIAL_WIZARD_DATA__?.companySettings?.default_locale ||
          window.__WIZARD_DATA__?.companySettings?.default_locale)) ||
      null;
    const shouldAdoptUserLocale =
      nextLocale &&
      nextLocale !== prevLocale &&
      !localStorage.getItem('findbob_language') &&
      (!companyDefaultLocale || nextLocale === companyDefaultLocale);
    if (shouldAdoptUserLocale && nextLocale !== this.state.currentLanguage) {
      this.setState({ currentLanguage: nextLocale });
    }
  }
  
  componentWillUnmount() {
    // Restore default scroll restoration on unmount
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'auto';
    }
    
    // Unsubscribe from Redux store
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }
  
  processGraphQLData() {
    const { graphqlData, store, wizardData } = this.props;
    
    // Check if user is authenticated and has valid data
    const isAuthenticated = graphqlData?.currentUserInfoAuth;
    const hasValidUser = graphqlData?.getCurrentUser && (graphqlData.getCurrentUser.id || graphqlData.getCurrentUser.email);
    
    // If user is not authenticated or missing critical data, reset to step 1
    if (graphqlData && (!isAuthenticated || !hasValidUser)) {
      const currentState = store.getState();
      const companySettings = currentState.companySettings || {};
      const companyId = companySettings?.id || companySettings?.name || 'default';
      
      // Clear saved progress and reset to step 1
      clearOnboardingProgress(companyId);
      store.dispatch({ type: 'SET_CURRENT_STEP', payload: 1 });
      
      // Update URL to reflect reset
      window.history.replaceState(
        { step: 1 }, 
        'Onboarding - Contact Information', 
        '/onboarding/contact_information'
      );
      
      return; // Exit early, don't process further
    }
    
    // Merge user data from GraphQL with Redux store data
    if (graphqlData && graphqlData.getCurrentUser) {
      // Stash the server-authoritative MFA requirement so getVisibleSteps (here
      // and in the module-level popstate handler) shows/hides the MFA step
      // consistently. Set before the auto-advance logic below reads visible steps.
      if (!window.__WIZARD_DATA__) window.__WIZARD_DATA__ = {};
      window.__WIZARD_DATA__.mfaRequired = !!graphqlData.getCurrentUser.mfa_enrollment_required;

      store.dispatch({
        type: 'UPDATE_USER_DATA',
        payload: graphqlData.getCurrentUser
      });
    }
    
    if (graphqlData && graphqlData.company) {
      store.dispatch({ 
        type: 'UPDATE_COMPANY_SETTINGS', 
        payload: {
          ...graphqlData.company,
          // company.features is often a settings hash; company.feature_types is the enabled feature flag list
          features: graphqlData.company.features || {},
          feature_types: graphqlData.company.feature_types || []
        }
      });
      
      // Update window.__WIZARD_DATA__ with GraphQL data
      if (window.__WIZARD_DATA__) {
        window.__WIZARD_DATA__.companySettings = {
          ...window.__WIZARD_DATA__.companySettings,
          ...graphqlData.company,
          features: graphqlData.company.features || (window.__WIZARD_DATA__.companySettings.features || {}),
          feature_types: graphqlData.company.feature_types || []
        };
      }
    }
    
    // Auto-advance steps based on completion status if not specifically starting at a step
    // Note: startAtStep is passed as a separate prop from OnboardingWizard (which gets it from window.__INITIAL_WIZARD_DATA__)
    if (!this.props.startAtStep && graphqlData?.getCurrentUser) {
      const user = graphqlData.getCurrentUser;
      const { currentStep } = this.state.storeState;
      const companySettings = this.state.storeState.companySettings || {};
      const companyId = companySettings?.id || companySettings?.name || 'default';
      const skipMotivation = isModernMotivationModalEnabledFromCompanySettings(companySettings);
      const savedProgress = getOnboardingProgressData(companyId);
      const savedStep = savedProgress
        ? (skipMotivation && !savedProgress.skipMotivation
          ? mapLegacyStepToVisibleStep({ legacyStep: savedProgress.step, businessType: companySettings.business_type, skipMotivation })
          : savedProgress.step)
        : 0;
      
      const filteredSteps = getVisibleSteps({ businessType: companySettings.business_type, skipMotivation });
      
      // If we have a valid saved step that's greater than the current step, use that
      if (savedStep > currentStep && savedStep <= filteredSteps.length) {
        store.dispatch({ type: 'SET_CURRENT_STEP', payload: savedStep });
        return;
      }
      
      // Only auto-advance if no saved progress exists (don't override user's saved progress)
      if (!savedStep || savedStep <= 1) {
        // Only check for completed_step_1 which exists in the schema
        if (user.completed_step_1 && currentStep < 2) {
          store.dispatch({ type: 'SET_CURRENT_STEP', payload: 2 });
        }
        
        // For steps 2 and 3, rely on resumeData only when motivation step exists
        if (!skipMotivation && window.__WIZARD_DATA__?.resumeData) {
          const resumeData = window.__WIZARD_DATA__.resumeData;
          
          // If motivation data exists and we're on step 1 or 2, move to step 3
          if (resumeData.primary_motivation && 
              resumeData.motivation_sub_options && 
              resumeData.motivation_sub_options.length > 0 && 
              currentStep < 3) {
            store.dispatch({ type: 'SET_CURRENT_STEP', payload: 3 });
          }
        }
      }
    }
  }
  
  goToNextStep() {
    const { currentStep, userData } = this.state.storeState;
    const { store } = this.props;
    const companySettings = getEffectiveCompanySettings(this.state.storeState.companySettings);
      const skipMotivation = isModernMotivationModalEnabledFromCompanySettings(companySettings);
    
    // Check if user data is still valid before allowing navigation
    if (!isUserDataValid(userData) && currentStep > 1) {
      const companyId = companySettings?.id || companySettings?.name || 'default';
      resetOnboardingForInvalidUser(store, companyId);
      return;
    }

    // Leaving contact info: if this tenant requires MFA, surface the MFA step as
    // step 2 right away. create_new_user just created the Identity, so a
    // brand-new advisor is by definition not yet enrolled — we don't need to
    // wait for the async user query to re-resolve. The server backstop remains
    // the authority; an already-enrolled user who somehow lands here gets a
    // no-op step that auto-advances (mfaEnrollmentSetup returns null).
    if (currentStep === 1 && companyRequiresMfa(companySettings)) {
      if (!window.__WIZARD_DATA__) window.__WIZARD_DATA__ = {};
      window.__WIZARD_DATA__.mfaRequired = true;
    }

    const filteredSteps = getVisibleSteps({ businessType: companySettings.business_type, skipMotivation });

    if (currentStep < filteredSteps.length) {
      const nextStep = currentStep + 1;
      store.dispatch({ type: 'SET_CURRENT_STEP', payload: nextStep });

      // Find the actual next step in the filtered array
      const nextStepData = filteredSteps[nextStep - 1];
      
      if (nextStepData && nextStepData.path) {
        // Update URL to match the step (for better UX and bookmark/reload support)
        window.history.replaceState(
          { step: nextStep }, 
          `Onboarding - ${nextStepData.name}`, 
          `/onboarding/${nextStepData.path}`
        );
      }
      
      // Scroll to top of page when navigating to next step
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 0);
    } else {
      // Final step completed, redirect to dashboard
      // Clear company-specific onboarding progress when complete
      const companyId = companySettings?.id || companySettings?.name || 'default';
      clearOnboardingProgress(companyId);
      window.location.href = '/succession/dashboard';
    }
  }
  
  goToPreviousStep = () => {
    const state = this.props.store.getState();
    const currentStep = state.currentStep;
    const userData = state.userData;
    const companySettings = getEffectiveCompanySettings(state.companySettings);
    const skipMotivation = isModernMotivationModalEnabledFromCompanySettings(companySettings);
    
    // Prevent going back to step 1 (contact information)
    // Once user completes contact info, they shouldn't be able to go back to it
    if (currentStep <= 2) {
      console.log('Cannot navigate back to contact information step');
      return false;
    }
    
    // For backward navigation, only reset if user data is completely missing (not just incomplete)
    // This allows users to navigate back during onboarding even if their profile isn't complete yet
    if (currentStep > 2 && userData && !userData.id && !userData.email && Object.keys(userData).length === 0) {
      const companyId = companySettings?.id || companySettings?.name || 'default';
      resetOnboardingForInvalidUser(this.props.store, companyId);
      return false;
    }
    
    const filteredSteps = getVisibleSteps({ businessType: companySettings.business_type, skipMotivation });
    
    if (currentStep > 1) {
      const prevStep = currentStep - 1;
      this.props.store.dispatch({ type: 'SET_CURRENT_STEP', payload: prevStep });
      
      // Update URL to match the step
      const prevStepData = filteredSteps[prevStep - 1];
      if (prevStepData && prevStepData.path) {
        window.history.replaceState(
          { step: prevStep }, 
          `Onboarding - ${prevStepData.name}`, 
          `/onboarding/${prevStepData.path}`
        );
      }
      
      // Scroll to top of page when navigating to previous step
      // Use setTimeout with longer delay to ensure DOM has fully updated
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      }, 100);
      return true;
    }
    return false;
  }
  
  handleUserUpdate(newUserData) {
    this.props.store.dispatch({ type: 'UPDATE_USER_DATA', payload: newUserData });
  }
  
  // Add language change handler
  handleLanguageChange(newLanguage) {
    this.setState({ currentLanguage: newLanguage });

    // Only persist an explicit override. If the user picks the locale the
    // tenant would already serve (company.default_locale), clear any stored
    // preference so stale entries don't shadow future tenant changes or
    // cross-tenant browsing. If they pick something different, remember it.
    const companyDefaultLocale =
      this.props.wizardData?.companySettings?.default_locale ||
      this.props.companySettings?.default_locale ||
      (typeof window !== 'undefined' &&
        (window.__INITIAL_WIZARD_DATA__?.companySettings?.default_locale ||
          window.__WIZARD_DATA__?.companySettings?.default_locale)) ||
      null;
    if (companyDefaultLocale && newLanguage === companyDefaultLocale) {
      localStorage.removeItem('findbob_language');
    } else {
      localStorage.setItem('findbob_language', newLanguage);
    }
    
    // Update user data if available
    const { userData } = this.state.storeState;
    const { currentStep } = this.state.storeState;
    const companySettings = this.state.storeState.companySettings || {};
    const skipMotivation = isModernMotivationModalEnabledFromCompanySettings(companySettings);
    
    // Get the current step path to preserve it
    const filteredSteps = getVisibleSteps({ businessType: companySettings.business_type, skipMotivation });
    
    const currentStepData = filteredSteps[currentStep - 1];
    const currentPath = currentStepData ? currentStepData.path : '';
    
    if (userData && userData.id) {
      // Add locale to user data
      const updatedUserData = {
        ...userData,
        locale: newLanguage
      };
      
      // Update in Redux store
      this.handleUserUpdate(updatedUserData);
      
      // If Apollo client is available, update in database
      if (this.props.client) {
        this.props.client.mutate({
          mutation: gql`
            mutation updateCurrentUser($locale: String) {
              updateCurrentUser(locale: $locale)
            }
          `,
          variables: { locale: newLanguage }
        }).then(() => {
          // After updating, reload the page with the current step path
          if (currentPath) {
            window.location.href = `/onboarding/${currentPath}`;
          } else {
            window.location.reload();
          }
        }).catch(error => {
          console.error("Error updating user locale:", error);
          // Still reload to show the language change
          if (currentPath) {
            window.location.href = `/onboarding/${currentPath}`;
          } else {
            window.location.reload();
          }
        });
      } else {
        // No client, just reload with current path
        if (currentPath) {
          window.location.href = `/onboarding/${currentPath}`;
        } else {
          window.location.reload();
        }
      }
    } else {
      // No user data, just reload with current path
      if (currentPath) {
        window.location.href = `/onboarding/${currentPath}`;
      } else {
        window.location.reload();
      }
    }
  }
  
  handleHideHeader(shouldHide) {
    this.setState({ hideHeader: shouldHide });
  }
  
  render() {
    const { graphqlData, graphqlLoading, client } = this.props;
    const { storeState, currentLanguage, hideHeader } = this.state;
    const { currentStep, userData } = storeState;
    // Use effective settings to ensure flags are present
    const companySettings = getEffectiveCompanySettings(storeState.companySettings);
    
    const skipMotivation = isModernMotivationModalEnabledFromCompanySettings(companySettings);
    // Filter steps based on company settings and modernized motivation flow
    const filteredSteps = getVisibleSteps({ businessType: companySettings.business_type, skipMotivation }).map(step => ({
      ...step,
      // Use translated name based on current language
      displayName: currentLanguage === 'fr' && step.fr_name ? step.fr_name : step.name
    }));
    
    // Recalculate currentStep if needed based on filtered steps.
    // Note: store `currentStep` is already tracked in the visible-steps index (even when skipping Motivation).
    let adjustedCurrentStep = Math.min(Math.max(1, currentStep), filteredSteps.length);

    // Find the current step component based on adjusted step number
    const currentStepIndex = Math.min(adjustedCurrentStep - 1, filteredSteps.length - 1);
    let CurrentStepComponent = filteredSteps[currentStepIndex]?.component;
    
    // For the first step, use the create user component if there's no current user
    if (adjustedCurrentStep === 1) {
      const hasUser = userData && userData.id;
      CurrentStepComponent = hasUser ? ContactInformationStep : ContactInformationCreateUserStep;
    }

    // require_identity_activation (361): a sign-up waiting for its emailed link
    // sees "Check your email" at step 1 instead of the sign-up form.
    const activationPending = !(userData && userData.id) &&
      (this.props.wizardData?.activationPending || window.__INITIAL_WIZARD_DATA__?.activationPending);
    if (activationPending) {
      return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <ProgressIndicator
            currentStep={1}
            totalSteps={filteredSteps.length}
            steps={filteredSteps.map(step => step.displayName)}
            onStepClick={() => {}}
          />
          {/* Same height as the sign-up form card it replaces, so the wizard doesn't collapse. */}
          <div
            className="bg-white shadow-xl rounded-2xl p-10 mt-6 border border-gray-100"
            style={{ minHeight: 1425 }}
          >
            <ActivationCheckEmailPage {...activationPending} embedded />
          </div>
        </div>
      );
    }

    // Return early if no component is found
    if (!CurrentStepComponent) {
      return <div>Loading...</div>;
    }

    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <ProgressIndicator 
          currentStep={adjustedCurrentStep} 
          totalSteps={filteredSteps.length}
          steps={filteredSteps.map(step => step.displayName)}
          onStepClick={(stepNum) => {
            if (stepNum < adjustedCurrentStep) {
              this.goToStep(stepNum);
            }
          }}
        />
        <div className="bg-white shadow-xl rounded-2xl p-10 mt-6 border border-gray-100">
          {/* Add common header component - only show for step 1 (contact info steps) and when not showing success message */}
          {!hideHeader && adjustedCurrentStep === 1 && (
            <OnboardingHeader 
              currentLanguage={this.state.currentLanguage}
              onLanguageChange={this.handleLanguageChange}
              companySettings={companySettings}
            />
          )}
          
          <CurrentStepComponent 
            user={userData}
            companySettings={companySettings}
            onUserUpdate={this.handleUserUpdate}
            onNext={this.goToNextStep}
            onBack={this.goToPreviousStep}
            privacyPolicy={graphqlData?.getPrivacyPolicy}
            termsOfService={graphqlData?.getTermsOfService}
            resumeData={window.__WIZARD_DATA__?.resumeData}
            currentLanguage={this.state.currentLanguage}
            onHideHeader={CurrentStepComponent === ContactInformationCreateUserStep ? this.handleHideHeader : undefined}
            // Pass mutations and data for creation/update steps
            updateUser={CurrentStepComponent === ContactInformationStep && this.props.client ? 
              ({ variables }) => {
                return this.props.client.mutate({
                  mutation: gql`
                    mutation updateCurrentUser(
                      $postalCode: String
                      $phone: String
                      $email: String
                      $name: String
                      $birthDate: String
                      $completedStep1: Boolean
                      $acceptedPolicies: Boolean
                    ) {
                      updateCurrentUser(
                        postal_code: $postalCode
                        phone: $phone
                        email: $email
                        name: $name
                        birth_date: $birthDate
                        completed_step_1: $completedStep1
                        accepted_policies: $acceptedPolicies
                      )
                    }
                  `,
                  variables: {
                    postalCode: variables.postal_code,
                    phone: variables.phone,
                    email: variables.email,
                    name: variables.name,
                    birthDate: variables.birth_date,
                    completedStep1: variables.completed_step_1,
                    acceptedPolicies: variables.accepted_policies
                  }
                });
              } : undefined
            }
            createUser={CurrentStepComponent === ContactInformationCreateUserStep && this.props.client ? 
              ({ variables }) => {
                return this.props.client.mutate({
                  mutation: gql`
                    mutation createNewUser(
                      $agencyName: String
                      $fullName: String!
                      $email: String!
                      $birthDate: String!
                      $password: String!
                      $postal: String!
                      $phone: String
                      $locale: String!
                      $ref: String
                      $explicit_opt_in_tos_timestamp: String
                      $accepted_policies: Boolean
                    ) {
                      createNewUser(
                        agencyName: $agencyName
                        fullName: $fullName
                        email: $email
                        birthDate: $birthDate
                        password: $password
                        postal: $postal
                        phone: $phone
                        locale: $locale
                        ref: $ref
                        explicit_opt_in_tos_timestamp: $explicit_opt_in_tos_timestamp
                        accepted_policies: $accepted_policies
                      )
                    }
                  `,
                  variables: variables
                });
              } : undefined
            }
            // Pass data to PracticeInformationStep when it's that step
            availablePrimaryMarkets={CurrentStepComponent === PracticeInformationStep ? 
              (userData?.company?.available_primary_markets || graphqlData?.company?.available_primary_markets || []) : 
              undefined}
            availableDesignations={CurrentStepComponent === PracticeInformationStep ? 
              graphqlData?.availableDesignations || [] : 
              undefined}
            availableAdvisorLicenses={CurrentStepComponent === PracticeInformationStep ? 
              graphqlData?.availableAdvisorLicenses || [] : 
              undefined}
            availableStrengths={CurrentStepComponent === PracticeInformationStep ? 
              graphqlData?.availableStrengths || [] : 
              undefined}
            iirocLicenseOptions={CurrentStepComponent === PracticeInformationStep ? 
              (userData?.company?.iiroc_licensing_options || graphqlData?.company?.iiroc_licensing_options || false) : 
              undefined}
            // Revenue cap + labels for ProductionInformationStep come from this query's company.
            // A second company query with an `id` breaks this one's cache writes (apollo-client 2).
            companyRevenueSettings={CurrentStepComponent === ProductionInformationStep ?
              { loading: graphqlLoading, company: graphqlData?.company } :
              undefined}
            availableAvatars={CurrentStepComponent === HeadlineAndAboutMeStep ?
              graphqlData?.availableAvatars || [] :
              undefined}
            client={this.props.client}
          />
        </div>
      </div>
    );
  }
}

// GraphQL query component - using class component
class OnboardingWizardWithData extends React.Component {
  render() {
    return (
      <Query query={USER_QUERY} errorPolicy="all">
        {({ loading, error, data, refetch }) => {
          // Show loading state
          // Loading state is handled by the component
          
          // Log any GraphQL errors but still proceed
          if (error) {
            console.error('GraphQL error:', error);
          }
          
          return (
            <OnboardingWizardInner 
              graphqlData={data}
              graphqlError={error}
              graphqlLoading={loading}
              refetchData={refetch}
              store={this.props.store}
              wizardData={this.props.wizardData}
              startAtStep={this.props.startAtStep}
              client={this.props.client}
            />
          );
        }}
      </Query>
    );
  }
}

// Resume Progress Banner Component
const ResumeProgressBanner = ({ currentStep, goToStep, onDismiss, userData = {} }) => {
  const stepNames = (window.__WIZARD_DATA__?.visibleSteps || STEPS).map(step => step.name);
  
  // Don't show banner if user data is invalid
  if (!isUserDataValid(userData)) {
    return null;
  }
  
  return (
    <div className="fixed inset-x-0 bottom-0 z-50">
      <div className="bg-indigo-600 text-white p-4 shadow-lg">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <p className="font-medium">Welcome back to onboarding!</p>
            <p className="text-sm text-indigo-100">
              You were previously on step {currentStep}: {stepNames[currentStep-1]}. 
              Would you like to continue where you left off?
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => goToStep(1)}
              className="px-3 py-1 text-sm bg-transparent border border-white rounded-md hover:bg-indigo-700"
            >
              Start Over
            </button>
            <button
              onClick={() => goToStep(currentStep)}
              className="px-3 py-1 text-sm bg-white text-indigo-600 rounded-md hover:bg-indigo-100"
            >
              Continue
            </button>
            <button
              onClick={onDismiss}
              className="text-indigo-200 hover:text-white"
              aria-label="Dismiss"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main component wrapper - using class component
class OnboardingWizard extends React.Component {
  constructor(props) {
    super(props);
    
    // Clean up any expired onboarding progress on initialization
    cleanupExpiredOnboardingProgress();
    
    // Get initial wizard data (props can be missing/partial in some logged-out flows)
    const wizardDataFromProps = props.wizardData || {};
    const wizardDataFromWindow = window.__INITIAL_WIZARD_DATA__ || window.__WIZARD_DATA__ || {};
    
    // IMPORTANT: ReactOnRails sometimes passes empty objects {} instead of actual data.
    // Check if objects have keys before using them.
    const propsCompanySettings = wizardDataFromProps.companySettings;
    const windowCompanySettings = wizardDataFromWindow.companySettings;
    const hasPropsSettings = propsCompanySettings && Object.keys(propsCompanySettings).length > 0;
    const hasWindowSettings = windowCompanySettings && Object.keys(windowCompanySettings).length > 0;
    const companySettings = hasPropsSettings ? propsCompanySettings : (hasWindowSettings ? windowCompanySettings : {});
    
    // Same pattern for user data
    const propsUser = wizardDataFromProps.user;
    const windowUser = wizardDataFromWindow.user;
    const hasPropsUser = propsUser && Object.keys(propsUser).length > 0;
    const hasWindowUser = windowUser && Object.keys(windowUser).length > 0;
    const userFromProps = hasPropsUser ? propsUser : (hasWindowUser ? windowUser : {});
    
    const resumeDataFromProps = wizardDataFromProps.resumeData || {};
    const resumeDataFromWindow = wizardDataFromWindow.resumeData || {};
    const wizardData = {
      ...wizardDataFromProps,
      user: userFromProps,
      companySettings,
      resumeData: Object.keys(resumeDataFromProps).length ? resumeDataFromProps : resumeDataFromWindow,
    };

    const businessType = companySettings?.business_type || 'other';
    const skipMotivation = isModernMotivationModalEnabledFromCompanySettings(companySettings);
    const visibleSteps = getVisibleSteps({ businessType, skipMotivation });
    
    // Check URL for step information first
    const pathMatch = window.location.pathname.match(/\/onboarding\/([^\/]+)/);
    const path = pathMatch ? pathMatch[1] : null;
    const urlStep = path ? findStepByPath(path, visibleSteps) : null;
    
    
    // Check if user has missing or incomplete information
    const hasValidUserData = wizardData.user && (wizardData.user.id || wizardData.user.email);
    
    // Get startAtStep from props (comes from server's determine_initial_step which reads user.onboarding_progress)
    // IMPORTANT: ReactOnRails sometimes doesn't pass props correctly, so fall back to window data
    const propsStartAtStep = props.startAtStep;
    const windowStartAtStep = window.__INITIAL_WIZARD_DATA__?.startAtStep;
    const effectiveStartAtStep = propsStartAtStep || windowStartAtStep;
    const serverStep = effectiveStartAtStep ? parseInt(effectiveStartAtStep) : null;
    
    // Store for use in render method and componentDidMount
    this.effectiveStartAtStep = serverStep;
    
    // Get company-specific saved step with timeout validation
    const companyId = companySettings?.id || companySettings?.name || 'default';
    const savedProgress = getOnboardingProgressData(companyId);
    const savedStep = savedProgress
      ? (skipMotivation && !savedProgress.skipMotivation
        ? mapLegacyStepToVisibleStep({ legacyStep: savedProgress.step, businessType, skipMotivation })
        : savedProgress.step)
      : null;
    // Show banner if we have saved progress (from server or localStorage) AND valid user data and it's different from URL step
    const effectiveSavedStep = serverStep || savedStep;
    const hasProgress = hasValidUserData && effectiveSavedStep && effectiveSavedStep > 1 && (!urlStep || urlStep !== effectiveSavedStep);
    
    this.state = {
      showProgressBanner: hasProgress
    };
    
    const filteredSteps = visibleSteps;
    
    // Determine initial step priority:
    // 1. URL step (direct navigation)
    // 2. Server-side step (from user.onboarding_progress - persists across sessions)
    // 3. localStorage saved step (expires after 30 min)
    // 4. Default to 1
    let initialStep = 1;
    if (urlStep && urlStep >= 1 && urlStep <= filteredSteps.length) {
      initialStep = urlStep;
    } else if (hasValidUserData && serverStep && serverStep >= 1 && serverStep <= filteredSteps.length) {
      // Use server-side progress if available (persists across sessions)
      initialStep = serverStep;
    } else if (hasValidUserData && savedStep && savedStep >= 1 && savedStep <= filteredSteps.length) {
      // Fall back to localStorage (short-term persistence)
      initialStep = savedStep;
    } else if (!hasValidUserData && (savedStep || serverStep)) {
      // Clear saved progress if user data is missing/invalid
      clearOnboardingProgress(companyId);
      initialStep = 1;
    }
    
    // Create Redux store with initial state
    this.store = createStore(wizardReducer, {
      currentStep: initialStep,
      userData: wizardData.user || {},
      companySettings: companySettings || { business_type: businessType }
    });
    
    // Create Apollo client
    const httpLink = createHttpLink({
      uri: '/graphql',
      credentials: 'same-origin',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      }
    });
    
    this.client = new ApolloClient({
      link: httpLink,
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'network-only',
          errorPolicy: 'ignore',
        },
        query: {
          fetchPolicy: 'network-only',
          errorPolicy: 'all',
        },
        mutate: {
          errorPolicy: 'all'
        }
      },
    });

    // Expose navigation helpers globally for direct URL changes
    window.FindBobOnboarding = {
      saveCurrentStep,
      resetOnboardingProgress
    };
  }
  
  componentDidMount() {
    // Disable browser scroll restoration to prevent scroll position preservation on navigation
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // Scroll to top on mount
    window.scrollTo(0, 0);
    
    const { wizardData, startAtStep } = this.props;
    const wizardDataFromWindow = window.__INITIAL_WIZARD_DATA__ || window.__WIZARD_DATA__ || {};
    const effectiveWizardData = wizardData || wizardDataFromWindow || {};
    
    // Initialize state with provided wizardData
    if (effectiveWizardData) {
      this.store.dispatch({ type: 'UPDATE_USER_DATA', payload: effectiveWizardData.user || {} });
      this.store.dispatch({ type: 'UPDATE_COMPANY_SETTINGS', payload: effectiveWizardData.companySettings || {} });
    }
    
    // Only override step if startAtStep is provided AND user didn't navigate to a specific URL
    // (URL step should have highest priority since user explicitly navigated there)
    const pathMatch = window.location.pathname.match(/\/onboarding\/([^\/]+)/);
    const hasUrlStep = pathMatch && pathMatch[1] && pathMatch[1] !== 'index';
    
    // Use effectiveStartAtStep which was computed in constructor from props or window data
    if (this.effectiveStartAtStep && !hasUrlStep) {
      this.store.dispatch({ type: 'SET_CURRENT_STEP', payload: this.effectiveStartAtStep });
    }
    
    // Initialize global wizard data for cross-component access
    if (!window.__WIZARD_DATA__) {
      window.__WIZARD_DATA__ = {
        resumeData: effectiveWizardData?.resumeData || {},
        companySettings: effectiveWizardData?.companySettings || {
          company_type: effectiveWizardData?.companySettings?.company_type || 'individual',
          country: effectiveWizardData?.companySettings?.country || 'US',
          features: effectiveWizardData?.companySettings?.features || [],
          feature_types: effectiveWizardData?.companySettings?.feature_types || []
        }
      };
    }
    // Expose visible steps for banner copy consistency
    const state = this.store.getState();
    const businessType = state.companySettings?.business_type || effectiveWizardData?.companySettings?.business_type || 'other';
    const skipMotivation = isModernMotivationModalEnabledFromCompanySettings(state.companySettings || effectiveWizardData?.companySettings);
    window.__WIZARD_DATA__.visibleSteps = getVisibleSteps({ businessType, skipMotivation });
    
    // Set up periodic authentication check to detect logout
    this.authCheckInterval = setInterval(() => {
      const state = this.store.getState();
      const userData = state.userData;
      const currentStep = state.currentStep;
      
      // If user was logged in but now doesn't have valid data and not on step 1
      if (currentStep > 1 && !isUserDataValid(userData)) {
        const companySettings = state.companySettings || {};
        const companyId = companySettings?.id || companySettings?.name || 'default';
        
        resetOnboardingForInvalidUser(this.store, companyId);
        
        // Clear the interval since we're resetting
        if (this.authCheckInterval) {
          clearInterval(this.authCheckInterval);
          this.authCheckInterval = null;
        }
      }
    }, 30000); // Check every 30 seconds
    
    // Register global navigation functions for context integration
    window.onboardingGoToNextStep = this.goToNextStep;
    window.onboardingGoToPreviousStep = this.goToPreviousStep;
    window.onboardingUpdateUser = this.updateUser;
  }
  
  componentWillUnmount() {
    // Restore default scroll restoration on unmount
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'auto';
    }
    
    // Clean up periodic authentication check
    if (this.authCheckInterval) {
      clearInterval(this.authCheckInterval);
      this.authCheckInterval = null;
    }
    
    // Clean up global navigation functions
    delete window.onboardingGoToNextStep;
    delete window.onboardingGoToPreviousStep;
    delete window.onboardingUpdateUser;
  }
  
  dismissProgressBanner = () => {
    this.setState({ showProgressBanner: false });
  }
  
  goToStep = (step) => {
    const stepNumber = parseInt(step);
    const state = this.store.getState();
    const businessType = state.companySettings?.business_type || this.props.wizardData?.companySettings?.business_type || 'other';
    const skipMotivation = isModernMotivationModalEnabledFromCompanySettings(state.companySettings);
    const filteredSteps = getVisibleSteps({ businessType, skipMotivation });
    
    if (stepNumber && stepNumber >= 1 && stepNumber <= filteredSteps.length) {
      // Save to company-specific localStorage
      const companyId = state.companySettings?.id || state.companySettings?.name || 'default';
      saveOnboardingProgress(stepNumber, companyId);
      
      // Update store
      this.store.dispatch({ type: 'SET_CURRENT_STEP', payload: stepNumber });
      
      // Update URL to match the step without reloading
      const stepData = filteredSteps[stepNumber - 1];
      if (stepData && stepData.path) {
        window.history.replaceState(
          { step: stepNumber }, 
          `Onboarding - ${stepData.name}`, 
          `/onboarding/${stepData.path}`
        );
      }
      
      // Close banner
      this.setState({ showProgressBanner: false });
    }
  }

  // Add context navigation methods that work with Redux
  goToNextStep = () => {
    const state = this.store.getState();
    const currentStep = state.currentStep;
    const userData = state.userData;
    const companySettings = this.getEffectiveCompanySettings();
    const skipMotivation = isModernMotivationModalEnabledFromCompanySettings(companySettings);
    
    // Check if user data is still valid before allowing navigation
    if (!isUserDataValid(userData) && currentStep > 1) {
      const companyId = companySettings?.id || companySettings?.name || 'default';
      resetOnboardingForInvalidUser(this.store, companyId);
      return false;
    }
    
    const filteredSteps = getVisibleSteps({ businessType: companySettings.business_type, skipMotivation });
    
    if (currentStep < filteredSteps.length) {
      const nextStep = currentStep + 1;
      this.store.dispatch({ type: 'SET_CURRENT_STEP', payload: nextStep });
      
      // Update URL to match the step
      const nextStepData = filteredSteps[nextStep - 1];
      if (nextStepData && nextStepData.path) {
        window.history.replaceState(
          { step: nextStep }, 
          `Onboarding - ${nextStepData.name}`, 
          `/onboarding/${nextStepData.path}`
        );
      }
      
      // Scroll to top of page when navigating to next step
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 0);
      return true;
    } else {
      // Final step completed, redirect to dashboard
      const companyId = companySettings?.id || companySettings?.name || 'default';
      clearOnboardingProgress(companyId);
      window.location.href = '/succession/dashboard';
      return true;
    }
  }
  
  goToPreviousStep = () => {
    const state = this.store.getState();
    const currentStep = state.currentStep;
    const userData = state.userData;
    const companySettings = this.getEffectiveCompanySettings();
    const skipMotivation = isModernMotivationModalEnabledFromCompanySettings(companySettings);
    
    // Prevent going back to step 1 (contact information)
    // Once user completes contact info, they shouldn't be able to go back to it
    if (currentStep <= 2) {
      console.log('Cannot navigate back to contact information step');
      return false;
    }
    
    // For backward navigation, only reset if user data is completely missing (not just incomplete)
    // This allows users to navigate back during onboarding even if their profile isn't complete yet
    if (currentStep > 2 && userData && !userData.id && !userData.email && Object.keys(userData).length === 0) {
      const companyId = companySettings?.id || companySettings?.name || 'default';
      resetOnboardingForInvalidUser(this.store, companyId);
      return false;
    }
    
    const filteredSteps = getVisibleSteps({ businessType: companySettings.business_type, skipMotivation });

    if (currentStep > 1) {
      const prevStep = currentStep - 1;
      this.store.dispatch({ type: 'SET_CURRENT_STEP', payload: prevStep });
      
      // Update URL to match the step
      const prevStepData = filteredSteps[prevStep - 1];
      if (prevStepData && prevStepData.path) {
        window.history.replaceState(
          { step: prevStep }, 
          `Onboarding - ${prevStepData.name}`, 
          `/onboarding/${prevStepData.path}`
        );
      }
      
      // Scroll to top of page when navigating to previous step
      // Use setTimeout with longer delay to ensure DOM has fully updated
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      }, 100);
      return true;
    }
    return false;
  }
  
  updateUser = (userData) => {
    // Validate user data before updating
    if (userData && !isUserDataValid(userData)) {
      console.warn('Attempting to update with invalid user data, ignoring update');
      return false;
    }
    
    this.store.dispatch({ type: 'UPDATE_USER_DATA', payload: userData });
    return true;
  }

  getEffectiveCompanySettings() {
    const state = this.store.getState();
    const storeSettings = state.companySettings || {};
    const windowSettings = window.__INITIAL_WIZARD_DATA__?.companySettings || window.__WIZARD_DATA__?.companySettings || {};
    
    // Merge strategies: prefer store, but fallback to window for arrays/objects if missing in store or empty
    // This protects against GraphQL updates potentially missing feature flags
    return {
      ...windowSettings,
      ...storeSettings,
      feature_types: (storeSettings.feature_types && storeSettings.feature_types.length > 0) 
        ? storeSettings.feature_types 
        : (windowSettings.feature_types || []),
      features: (storeSettings.features && (Array.isArray(storeSettings.features) ? storeSettings.features.length > 0 : Object.keys(storeSettings.features).length > 0))
        ? storeSettings.features
        : (windowSettings.features || {})
    };
  }
  
  render() {
    const { showProgressBanner } = this.state;
    const storeState = this.store.getState();
    const companySettings = this.getEffectiveCompanySettings();
    const skipMotivation = isModernMotivationModalEnabledFromCompanySettings(companySettings);
    const businessType = storeState.companySettings?.business_type || 'other';
    const visibleSteps = getVisibleSteps({ businessType, skipMotivation });
    // store currentStep is already tracked in the visible-steps index
    const currentStep = storeState.currentStep;
    const userData = storeState.userData;
    
    // Create context value to expose to step components
    const contextValue = {
      goToNextStep: this.goToNextStep,
      goToPreviousStep: this.goToPreviousStep,
      updateUser: this.updateUser,
      setStepValid: () => true, // No-op, handled by individual components
    };
    
    return (
      <Provider store={this.store}>
        <ApolloProvider client={this.client}>
          <OnboardingThemeProvider companySettings={storeState.companySettings}>
              {showProgressBanner && (
                <ResumeProgressBanner 
                  currentStep={currentStep} 
                  goToStep={this.goToStep} 
                  onDismiss={this.dismissProgressBanner}
                  userData={storeState.userData}
                />
              )}
              <OnboardingWizardWithData 
                store={this.store} 
                wizardData={this.props.wizardData} 
                startAtStep={this.effectiveStartAtStep} 
                client={this.client}
              />
          </OnboardingThemeProvider>
        </ApolloProvider>
      </Provider>
    );
  }
}

OnboardingWizard.propTypes = {
  wizardData: PropTypes.shape({
    user: PropTypes.object,
    companySettings: PropTypes.object,
    resumeData: PropTypes.object
  }),
  startAtStep: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

OnboardingWizard.defaultProps = {
  wizardData: {
    user: {},
    companySettings: {},
    resumeData: {}
  },
  startAtStep: null
};

// Wrapper function so React-on-Rails passes railsContext as the second arg.
// Class components don't receive railsContext directly, so we wire window.locale
// and window.currency here before the tree mounts (currencySymbol() reads them).
const OnboardingWizardEntry = (props, railsContext) => {
  if (typeof window !== 'undefined') {
    window.locale = (railsContext && railsContext.i18nLocale) || window.locale || 'en';
    window.currency = (railsContext && railsContext.currency) || window.currency || 'USD';
  }
  return <OnboardingWizard {...props} />;
};

export default OnboardingWizardEntry; 